<script>
import { mapGetters } from 'vuex';
import {
  phoneLog,
  phoneWarn,
  resolveSpeakerId,
  deviceRef,
  availableDevices,
} from 'dashboard/store/modules/phone';

// Chrome fires devicechange two to four times for a single plug or unplug, and
// enumerateDevices() on the first of them still reports a half-built list.
const DEVICE_CHANGE_DEBOUNCE = 400;

export default {
  name: 'PhoneAudioElements',
  computed: {
    ...mapGetters({
      streams: 'phone/getStreams',
      transferRemoteStream: 'phone/getTransferRemoteStream',
      silent: 'phone/getSpeakerSilent',
      selectedSpeaker: 'phone/getSelectedSpeakerId',
    }),
    // While a transfer leg has audio, that is what the agent is listening to:
    // the original call is on hold and silent. Clearing the leg (completed,
    // cancelled, target hung up) puts the call back on the element by itself.
    playbackStreams() {
      if (!this.transferRemoteStream) return this.streams;
      return {
        local: this.streams?.local || null,
        remote: this.transferRemoteStream,
      };
    },
    remoteElement() {
      return this.$refs.phoneRemoteAudio;
    },
    localElement() {
      return this.$refs.phoneLocalAudio;
    },
  },
  watch: {
    playbackStreams: {
      handler(newVal) {
        this.attachStreams(newVal);
      },
      deep: true,
    },
    selectedSpeaker() {
      // Re-route live (e.g. user picks a different output mid-call).
      this.applySinkId();
    },
  },
  mounted() {
    // The store outlives this component — the Salesforce panel re-creates it on
    // its own — so a call can already be up by the time we mount. The watcher
    // alone would never fire for it and the remote leg would stay silent for
    // the whole call. (`immediate: true` is no help: it runs before $refs.)
    this.attachStreams(this.playbackStreams);
    this.applySinkId();
    if (navigator.mediaDevices?.addEventListener) {
      navigator.mediaDevices.addEventListener(
        'devicechange',
        this.onDeviceChange
      );
    }
    // Waking from sleep is the classic way this whole audio path goes stale
    // without a single event firing: the capture is dead, the sink points at
    // nothing, and the agent finds out on their next call. Coming back to the
    // tab is the cheapest reliable moment to re-check.
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  },
  beforeUnmount() {
    clearTimeout(this.deviceChangeTimer);
    navigator.mediaDevices?.removeEventListener?.(
      'devicechange',
      this.onDeviceChange
    );
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  },
  methods: {
    attachStreams(streams) {
      if (this.remoteElement) {
        if (streams?.remote) {
          this.remoteElement.srcObject = streams.remote;
          // Route freshly-attached call audio to the chosen output device.
          this.applySinkId();
          this.remoteElement.play().catch(error => {
            // Autoplay policy, most likely. Swallowing this used to mean the
            // call looked healthy in the SIP logs while the agent heard
            // nothing and no trace of why existed anywhere.
            phoneWarn('Remote audio failed to start playing', error);
          });
        } else {
          this.remoteElement.srcObject = null;
        }
      }
      if (this.localElement) {
        this.localElement.srcObject = streams?.local || null;
      }
    },
    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      this.$store.dispatch('phone/revalidateAudioPath');
      this.applySinkId();
    },
    onDeviceChange() {
      clearTimeout(this.deviceChangeTimer);
      this.deviceChangeTimer = setTimeout(async () => {
        await this.$store.dispatch('phone/handleDeviceChange');
        await this.applySinkId();
      }, DEVICE_CHANGE_DEBOUNCE);
    },
    async applySinkId() {
      const el = this.remoteElement;
      // setSinkId is not supported everywhere (notably some Safari versions).
      if (!el || typeof el.setSinkId !== 'function') return;
      // Resolution is async now and this runs from four places (new stream,
      // speaker change, mount, device change) — a serial keeps a slow lookup
      // from overwriting a newer selection.
      this.sinkSerial = (this.sinkSerial || 0) + 1;
      const serial = this.sinkSerial;
      const desired = await resolveSpeakerId(this.selectedSpeaker);
      if (serial !== this.sinkSerial) return;
      try {
        // '' / 'default' both fall back to the system default output.
        await el.setSinkId(desired);
        await this.logSinkChange(desired);
      } catch (error) {
        phoneWarn(
          `setSinkId failed for ${deviceRef(desired)} — falling back to the default output`,
          error
        );
        if (!desired) return;
        // Leaving the element on a sink nobody is listening to is the whole
        // bug; the default output is always better than silence.
        try {
          await el.setSinkId('');
        } catch (fallbackError) {
          phoneWarn('Default output is unusable too', fallbackError);
        }
      }
    },
    // applySinkId re-runs for every new call stream, so log only an actual
    // change of output — by name, since the id alone tells a reader nothing.
    async logSinkChange(sinkId) {
      if (sinkId === this.loggedSinkId) return;
      this.loggedSinkId = sinkId;
      if (!sinkId) {
        phoneLog('Speaker output: system default');
        return;
      }
      const device = (await availableDevices('audiooutput')).find(
        d => d.deviceId === sinkId
      );
      phoneLog(
        `Speaker output: "${device?.label || 'unknown'}" (${deviceRef(sinkId)})`
      );
    },
  },
};
</script>

<template>
  <audio
    id="phoneRemoteAudio"
    ref="phoneRemoteAudio"
    autoplay
    :muted="silent"
  />
  <audio id="phoneLocalAudio" ref="phoneLocalAudio" muted />
</template>
